import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import { banMember, removeMember, changeMemberRole, approveJoinGroup, inviteMemberToGroup } from '_redux/modules/group';
import {
  SINGAPORE_CODE_NO_ADD,
  GROUP_IS_ADMIN,
  GROUP_IS_HOST,
  GROUP_IS_NOT_ADMIN,
  GROUP_IS_NOT_HOST,
} from '_utils/constant';
import { ROLE_TYPES } from '../constant';

export default function useMemberActions({
  groupId,
  groupInfo,
  groupName = '',
  userInfo = null,
  onMemberUpdated,
  onMemberInvited,
} = {}) {
  const dispatch = useDispatch();
  const { addToast } = useToasts();

  const handleWhatsApp = useCallback((member) => {
    if (!member?.phone) return;
    const cleanPhone = String(member.phone).replace(/\D/g, '');
    const waNumber = cleanPhone.startsWith('65')
      ? cleanPhone
      : `${SINGAPORE_CODE_NO_ADD}${cleanPhone}`;
    window.open(`https://wa.me/${waNumber}`, '_blank');
  }, []);

  const handleBan = useCallback(
    async (member) => {
      const userId = member?.uid;
      const targetGroupId = groupInfo?.id || groupId;
      if (!userId || !targetGroupId) return;

      const currentUserId = userInfo?.id || userInfo?.uid;
      const isSelf = Boolean(currentUserId && String(currentUserId) === String(userId));
      const isOwner = Boolean(
        groupInfo?.createdUserId && String(groupInfo.createdUserId) === String(userId)
      );
      if (isSelf || isOwner) {
        addToast(
          isOwner ? 'Cannot ban group owner' : 'Cannot ban your own account',
          { appearance: 'warning', autoDismiss: true }
        );
        return;
      }

      const isBanned = Boolean(member?.banned === 1 || member?.isBanned);
      const displayName = member?.uName || 'Member';

      if (typeof onMemberUpdated === 'function') {
        onMemberUpdated(userId, { banned: isBanned ? 0 : 1 });
      }

      try {
        await dispatch(
          banMember({
            groupId: targetGroupId,
            userId,
            banned: isBanned ? 1 : 0,
          })
        );

        const groupText = groupName ? ` from ${groupName}` : '';
        if (isBanned) {
          addToast(`${displayName} has been unban from the${groupText}`, {
            appearance: 'success',
            autoDismiss: true,
          });
        } else {
          addToast(`${displayName} has been banned from the${groupText}`, {
            appearance: 'success',
            autoDismiss: true,
          });
        }
      } catch (error) {
        if (typeof onMemberUpdated === 'function') {
          onMemberUpdated(userId, { banned: isBanned ? 1 : 0 });
        }
        addToast(error?.message || 'Failed to update ban status', {
          appearance: 'error',
          autoDismiss: true,
        });
        throw error;
      }
    },
    [dispatch, groupId, groupInfo, groupName, userInfo, addToast, onMemberUpdated]
  );

  const handleRemove = useCallback(
    async (member, onSuccess) => {
      const userId = member?.uid;
      const targetGroupId = groupInfo?.id || groupId;
      if (!userId || !targetGroupId) return;

      const currentUserId = userInfo?.id || userInfo?.uid;
      const isSelf = Boolean(currentUserId && String(currentUserId) === String(userId));
      const isOwner = Boolean(
        groupInfo?.createdUserId && String(groupInfo.createdUserId) === String(userId)
      );
      if (isSelf || isOwner) {
        addToast(
          isOwner ? 'Cannot remove group owner' : 'Cannot remove your own account',
          { appearance: 'warning', autoDismiss: true }
        );
        return;
      }

      const isConfirmed = window.confirm(`Are you sure you want to remove ${member?.uName}?`);
      if (!isConfirmed) return;

      try {
        await dispatch(
          removeMember({
            groupId: targetGroupId,
            userId,
          })
        );
        addToast('Remove member success', { appearance: 'success', autoDismiss: true });
        if (typeof onSuccess === 'function') {
          onSuccess(member);
        }
      } catch (error) {
        addToast(error?.message || 'Failed to remove member', {
          appearance: 'error',
          autoDismiss: true,
        });
      }
    },
    [dispatch, groupId, groupInfo, userInfo, addToast]
  );

  const handleChangeRole = useCallback(
    async (member, newRole) => {
      const userId = member?.uid;
      const targetGroupId = groupInfo?.id || groupId;
      if (!userId || !targetGroupId || !newRole) return;

      const currentUserId = userInfo?.id || userInfo?.uid;
      const isSelf = Boolean(currentUserId && String(currentUserId) === String(userId));
      const isOwner = Boolean(
        groupInfo?.createdUserId && String(groupInfo.createdUserId) === String(userId)
      );
      if (isSelf || isOwner) {
        addToast(
          isOwner ? 'Cannot change role of group owner' : 'Cannot change your own role',
          { appearance: 'warning', autoDismiss: true }
        );
        return;
      }

      const upperRole = newRole.toUpperCase();
      let roleFields = {
        role: upperRole,
        isAdmin: GROUP_IS_NOT_ADMIN,
        isHost: GROUP_IS_NOT_HOST,
      };

      if (upperRole === ROLE_TYPES.ADMIN) {
        roleFields = {
          role: ROLE_TYPES.ADMIN,
          isAdmin: GROUP_IS_ADMIN,
          isHost: GROUP_IS_HOST,
        };
      } else if (upperRole === ROLE_TYPES.HOST) {
        roleFields = {
          role: ROLE_TYPES.HOST,
          isAdmin: GROUP_IS_NOT_ADMIN,
          isHost: GROUP_IS_HOST,
        };
      }

      const prevFields = {
        role: member?.role,
        isAdmin: member?.isAdmin,
        isHost: member?.isHost,
      };

      if (typeof onMemberUpdated === 'function') {
        onMemberUpdated(userId, roleFields);
      }

      try {
        await dispatch(
          changeMemberRole({
            groupId: targetGroupId,
            userId,
            role: upperRole,
          })
        );
        addToast('Change role success', { appearance: 'success', autoDismiss: true });
      } catch (error) {
        if (typeof onMemberUpdated === 'function') {
          onMemberUpdated(userId, prevFields);
        }
        addToast(error?.message || 'Failed to change role', {
          appearance: 'error',
          autoDismiss: true,
        });
      }
    },
    [dispatch, groupId, groupInfo, userInfo, addToast, onMemberUpdated]
  );

  const handleAcceptMember = useCallback(
    async (member, onSuccess) => {
      const userId = member?.uid || member?.id || member?.userId;
      const targetGroupId = groupInfo?.id || groupId;
      if (!userId || !targetGroupId) return;

      const displayName = member?.name || member?.uName || 'Member';

      try {
        await dispatch(
          approveJoinGroup({
            groupId: targetGroupId,
            userId,
          })
        );
        addToast(`${displayName} was accepted`, {
          appearance: 'info',
          autoDismiss: true,
        });
        if (typeof onMemberUpdated === 'function') {
          onMemberUpdated(userId, { approval: 0, role: ROLE_TYPES.MEMBER });
        }
        if (typeof onSuccess === 'function') {
          onSuccess(member);
        }
      } catch (error) {
        addToast(error?.message || 'Failed to approve member', {
          appearance: 'error',
          autoDismiss: true,
        });
        throw error;
      }
    },
    [dispatch, groupId, groupInfo, userInfo, addToast, onMemberUpdated]
  );

  const handleRejectMember = useCallback(
    async (member, onSuccess) => {
      const userId = member?.uid || member?.id || member?.userId;
      const targetGroupId = groupInfo?.id || groupId;
      if (!userId || !targetGroupId) return;

      const displayName = member?.name || member?.uName || 'Member';

      try {
        await dispatch(
          removeMember({
            groupId: targetGroupId,
            userId,
          })
        );
        addToast(`${displayName} was rejected`, {
          appearance: 'warning',
          autoDismiss: true,
        });
        if (typeof onSuccess === 'function') {
          onSuccess(member);
        }
      } catch (error) {
        addToast(error?.message || 'Failed to reject member', {
          appearance: 'error',
          autoDismiss: true,
        });
        throw error;
      }
    },
    [dispatch, groupId, groupInfo, addToast]
  );

  const handleInviteMember = useCallback(
    async (payload, onSuccess) => {
      const targetGroupId = groupInfo?.id || groupId;
      if (!targetGroupId) return;

      const postData = {
        groupId: targetGroupId,
        email: payload?.email?.trim()?.toLowerCase(),
        role: payload?.role,
        message: payload?.message?.trim() || null,
      };

      try {
        await dispatch(inviteMemberToGroup(postData));
        addToast(`Invitation sent to ${postData.email}`, {
          appearance: 'success',
          autoDismiss: true,
        });

        if (typeof onMemberInvited === 'function') {
          await onMemberInvited();
        }

        if (typeof onSuccess === 'function') {
          onSuccess();
        }
      } catch (error) {
        addToast(error?.message || 'Failed to send invitation', {
          appearance: 'error',
          autoDismiss: true,
        });
        throw error;
      }
    },
    [dispatch, groupId, groupInfo, addToast, onMemberInvited]
  );

  return {
    handleWhatsApp,
    handleBan,
    handleRemove,
    handleChangeRole,
    handleAcceptMember,
    handleRejectMember,
    handleInviteMember,
  };
}
